/**
 * Document Management Module - Document Edit Page
 * 
 * Form for updating document metadata.
 * Note: File uploads create new versions, not updates to existing documents.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Breadcrumb, BreadcrumbItem, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { FaEdit, FaTimes, FaSpinner } from 'react-icons/fa';
import { DocumentCategory, Framework, RiskLevel } from './types';
import documentService from './documentService';
import './DocumentManagement.css';

const DocumentEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Policy' as DocumentCategory,
    framework: '' as Framework | '',
    controlIds: [] as string[],
    riskLevel: '' as RiskLevel | '',
    documentOwner: '',
    documentOwnerName: '',
    expiryDate: '',
    tags: [] as string[],
    folderId: '' as string,
  });
  
  const [tagInput, setTagInput] = useState('');

  // Standards/Frameworks state
  const [standards, setStandards] = useState<Array<{ id: string; name: string; code?: string | null; type: string }>>([]);
  const [loadingStandards, setLoadingStandards] = useState(false);

  // Folders state
  const [folders, setFolders] = useState<Array<{ id: string; name: string; path: string; parentId?: string }>>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);

  // Controls state with pagination
  const [controls, setControls] = useState<Array<{ id: string; code: string; name: string; description?: string }>>([]);
  const [loadingControls, setLoadingControls] = useState(false);
  const [controlsPage, setControlsPage] = useState(1);
  const [controlsTotalPages, setControlsTotalPages] = useState(0);
  const [controlsSearchTerm, setControlsSearchTerm] = useState('');
  const [showControlsModal, setShowControlsModal] = useState(false);
  const [selectedControlIds, setSelectedControlIds] = useState<Set<string>>(new Set());

  // Load document data
  useEffect(() => {
    const loadDocument = async () => {
      if (!id) {
        toast.error('Document ID is required');
        navigate('/documents');
        return;
      }

      setLoading(true);
      try {
        const doc = await documentService.getDocumentById(id);
        if (!doc) {
          toast.error('Document not found');
          navigate('/documents');
          return;
        }

        // Parse control IDs from comma-separated string
        const controlIds = doc.metadata.controlId 
          ? doc.metadata.controlId.split(',').map(c => c.trim()).filter(c => c)
          : [];

        // Format expiry date for input
        const expiryDate = doc.metadata.expiryDate
          ? new Date(doc.metadata.expiryDate).toISOString().split('T')[0]
          : '';

        setFormData({
          title: doc.metadata.title,
          description: doc.metadata.description || '',
          category: doc.metadata.category,
          framework: (doc.metadata.framework as Framework) || '',
          controlIds,
          riskLevel: (doc.metadata.riskLevel as RiskLevel) || '',
          documentOwner: doc.metadata.documentOwner || '',
          documentOwnerName: doc.metadata.documentOwnerName || '',
          expiryDate,
          tags: doc.metadata.tags || [],
          folderId: doc.metadata.folderId || '',
        });

        setSelectedControlIds(new Set(controlIds));
      } catch (error: any) {
        toast.error(error.message || 'Failed to load document');
        navigate('/documents');
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [id, navigate]);

  // Fetch standards and folders on component mount
  useEffect(() => {
    const fetchData = async () => {
      // Fetch standards
      setLoadingStandards(true);
      try {
        const standardsData = await documentService.getStandards();
        setStandards(standardsData);
      } catch (error: any) {
        toast.error('Failed to load standards: ' + error.message);
      } finally {
        setLoadingStandards(false);
      }

      // Fetch folders
      setLoadingFolders(true);
      try {
        const foldersData = await documentService.getFolders();
        setFolders(foldersData);
      } catch (error: any) {
        console.error('Failed to load folders:', error);
        toast.error('Failed to load folders: ' + (error.message || 'Unknown error'));
      } finally {
        setLoadingFolders(false);
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  // Fetch controls with pagination
  const fetchControls = async (page: number = 1, search: string = '') => {
    setLoadingControls(true);
    try {
      const result = await documentService.getControls(page, 10, search);
      if (page === 1) {
        setControls(result.data);
      } else {
        setControls(prev => [...prev, ...result.data]);
      }
      setControlsTotalPages(result.totalPages);
      setControlsPage(page);
    } catch (error: any) {
      toast.error('Failed to load controls: ' + error.message);
    } finally {
      setLoadingControls(false);
    }
  };

  // Load initial controls when control modal is opened
  useEffect(() => {
    if (showControlsModal && controls.length === 0) {
      fetchControls(1, controlsSearchTerm);
    }
  }, [showControlsModal]);

  // Handle controls search
  const handleControlsSearch = (searchTerm: string) => {
    setControlsSearchTerm(searchTerm);
    setControlsPage(1);
    setControls([]);
    fetchControls(1, searchTerm);
  };

  // Load more controls (pagination)
  const loadMoreControls = () => {
    if (controlsPage < controlsTotalPages && !loadingControls) {
      fetchControls(controlsPage + 1, controlsSearchTerm);
    }
  };

  // Handle control selection (toggle)
  const handleControlToggle = (control: { id: string; code: string; name: string }) => {
    const controlId = control.code || control.id;
    setSelectedControlIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(controlId)) {
        newSet.delete(controlId);
      } else {
        newSet.add(controlId);
      }
      return newSet;
    });
  };

  // Apply selected controls to form data
  const handleApplyControls = () => {
    setFormData(prev => ({ ...prev, controlIds: Array.from(selectedControlIds) }));
    setShowControlsModal(false);
  };

  // Reset controls when modal closes (cancel)
  const handleModalClose = () => {
    setSelectedControlIds(new Set(formData.controlIds));
    setShowControlsModal(false);
    setControlsSearchTerm('');
    setControlsPage(1);
    setControls([]);
  };

  // Initialize selected controls when modal opens
  useEffect(() => {
    if (showControlsModal) {
      setSelectedControlIds(new Set(formData.controlIds));
    }
  }, [showControlsModal, formData.controlIds]);

  // Remove a control from the list
  const removeControl = (controlId: string) => {
    setFormData(prev => ({
      ...prev,
      controlIds: prev.controlIds.filter(id => id !== controlId),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a document title');
      return;
    }

    if (!id) {
      toast.error('Document ID is missing');
      return;
    }

    setIsUpdating(true);
    try {
      const expiryDate = formData.expiryDate ? new Date(formData.expiryDate) : undefined;
      const tags = formData.tags.length > 0 ? formData.tags : undefined;

      // Build metadata object for update
      const metadata: any = {
        title: formData.title,
        description: formData.description || undefined,
        category: formData.category,
        framework: formData.framework ? (formData.framework as Framework) : undefined,
        controlId: formData.controlIds.length > 0 ? formData.controlIds.join(', ') : undefined,
        riskLevel: formData.riskLevel ? (formData.riskLevel as RiskLevel) : undefined,
        expiryDate,
        tags: tags && tags.length > 0 ? tags : undefined,
        folderId: formData.folderId || undefined,
      };

      await documentService.updateDocument(id, metadata);

      toast.success('Document updated successfully!');
      navigate(`/documents/${id}`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update document');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="document-management-container">
        <div className="container-fluid p-4" style={{ paddingTop: '2rem', marginTop: '100px', zoom: 0.9 }}>
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="document-management-container">
      <div className="container-fluid p-4" style={{ paddingTop: '2rem', marginTop: '100px', zoom: 0.9 }}>
        {/* Breadcrumb */}
        <Breadcrumb>
          <BreadcrumbItem>{t('header.you_are_here')}</BreadcrumbItem>
          <span className="mx-2">/</span>
          <BreadcrumbItem>
            <button
              className="btn btn-link p-0 text-decoration-none"
              onClick={() => navigate('/documents')}
            >
              Documents
            </button>
          </BreadcrumbItem>
          <span className="mx-2">/</span>
          <BreadcrumbItem active style={{ paddingRight: '10px' }}>
            Edit Document
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Edit Document</h4>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate(`/documents/${id}`)}
          >
            Cancel
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-lg-8">
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Document Information</h5>
                </div>
                <div className="card-body">
                  {/* Title */}
                  <div className="mb-3">
                    <label htmlFor="title" className="form-label">
                      Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  {/* Description */}
                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">
                      Description
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      rows={4}
                      value={formData.description}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Category */}
                  <div className="mb-3">
                    <label htmlFor="category" className="form-label">
                      Category <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="Policy">Policy</option>
                      <option value="Procedure">Procedure</option>
                      <option value="Evidence">Evidence</option>
                      <option value="Regulation">Regulation</option>
                    </select>
                  </div>

                  {/* Framework / Regulation */}
                  <div className="mb-3">
                    <label htmlFor="framework" className="form-label">
                      Framework / Regulation
                    </label>
                    {loadingStandards ? (
                      <div className="d-flex align-items-center gap-2">
                        <FaSpinner className="spinner-border spinner-border-sm" />
                        <span className="text-muted">Loading standards...</span>
                      </div>
                    ) : (
                      <select
                        className="form-select"
                        id="framework"
                        name="framework"
                        value={formData.framework}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Framework / Regulation</option>
                        {standards.map(standard => (
                          <option key={standard.id} value={standard.name}>
                            {standard.name} {standard.code ? `(${standard.code})` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Control ID */}
                  <div className="mb-3">
                    <label htmlFor="controlId" className="form-label">
                      Control ID
                    </label>
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control"
                        id="controlId"
                        value={formData.controlIds.join(', ')}
                        readOnly
                        placeholder="Click to select controls"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setShowControlsModal(true)}
                      >
                        Select Controls
                      </button>
                    </div>
                    {formData.controlIds.length > 0 && (
                      <div className="mt-2">
                        {formData.controlIds.map(controlId => (
                          <span key={controlId} className="badge bg-info me-1 mb-1">
                            {controlId}
                            <button
                              type="button"
                              className="btn-close btn-close-white ms-1"
                              style={{ fontSize: '0.7em' }}
                              onClick={() => removeControl(controlId)}
                              aria-label="Remove"
                            />
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Risk Level */}
                  <div className="mb-3">
                    <label htmlFor="riskLevel" className="form-label">
                      Risk Level
                    </label>
                    <select
                      className="form-select"
                      id="riskLevel"
                      name="riskLevel"
                      value={formData.riskLevel}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Risk Level</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>

                  {/* Folder */}
                  <div className="mb-3">
                    <label htmlFor="folderId" className="form-label">
                      Folder (Optional)
                    </label>
                    {loadingFolders ? (
                      <div className="d-flex align-items-center gap-2">
                        <FaSpinner className="spinner-border spinner-border-sm" />
                        <span className="text-muted">Loading folders...</span>
                      </div>
                    ) : (
                      <select
                        className="form-select"
                        id="folderId"
                        name="folderId"
                        value={formData.folderId}
                        onChange={handleInputChange}
                      >
                        <option value="">No Folder</option>
                        {folders.map(folder => (
                          <option key={folder.id} value={folder.id}>
                            {folder.path}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Expiry Date */}
                  <div className="mb-3">
                    <label htmlFor="expiryDate" className="form-label">
                      Expiry Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="expiryDate"
                      name="expiryDate"
                      value={formData.expiryDate}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Tags */}
                  <div className="mb-3">
                    <label htmlFor="tags" className="form-label">
                      Tags
                    </label>
                    <div className="d-flex gap-2 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        id="tags"
                        placeholder="Enter tag and press Enter or comma"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagInputKeyDown}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={addTag}
                      >
                        Add
                      </button>
                    </div>
                    {formData.tags.length > 0 && (
                      <div className="d-flex flex-wrap gap-2">
                        {formData.tags.map((tag, idx) => (
                          <span key={idx} className="badge bg-secondary d-flex align-items-center gap-1">
                            {tag}
                            <button
                              type="button"
                              className="btn-close btn-close-white"
                              style={{ fontSize: '0.7em' }}
                              onClick={() => removeTag(tag)}
                              aria-label="Remove"
                            />
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Actions</h5>
                </div>
                <div className="card-body">
                  <button
                    type="submit"
                    className="btn btn-primary w-100 mb-2"
                    disabled={isUpdating}
                  >
                    {isUpdating ? (
                      <>
                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <FaEdit className="me-2" />
                        Update Document
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100"
                    onClick={() => navigate(`/documents/${id}`)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Controls Selection Modal */}
        <Modal isOpen={showControlsModal} toggle={handleModalClose} size="lg">
          <ModalHeader toggle={handleModalClose}>Select Controls</ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search controls..."
                value={controlsSearchTerm}
                onChange={(e) => handleControlsSearch(e.target.value)}
              />
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {loadingControls && controls.length === 0 ? (
                <div className="text-center p-3">
                  <FaSpinner className="spinner-border spinner-border-sm" />
                  <span className="ms-2">Loading controls...</span>
                </div>
              ) : controls.length === 0 ? (
                <div className="text-center p-3 text-muted">No controls found</div>
              ) : (
                <>
                  {controls.map(control => (
                    <div
                      key={control.id}
                      className={`card mb-2 ${selectedControlIds.has(control.code || control.id) ? 'border-primary' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleControlToggle(control)}
                    >
                      <div className="card-body">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selectedControlIds.has(control.code || control.id)}
                            onChange={() => handleControlToggle(control)}
                          />
                          <label className="form-check-label">
                            <strong>{control.code}</strong> - {control.name}
                            {control.description && (
                              <div className="text-muted small">{control.description}</div>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                  {controlsPage < controlsTotalPages && (
                    <div className="text-center mt-3">
                      <button
                        className="btn btn-outline-primary"
                        onClick={loadMoreControls}
                        disabled={loadingControls}
                      >
                        {loadingControls ? (
                          <>
                            <FaSpinner className="spinner-border spinner-border-sm me-2" />
                            Loading...
                          </>
                        ) : (
                          'Load More'
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleApplyControls}>
              Apply ({selectedControlIds.size} selected)
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default DocumentEdit;

